import React, { useState } from "react";
import { Archive, ArchiveRestore, Flag, MoreVertical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useArchiveThreadMutation,
  useDeleteConversationPermanentlyMutation,
  useFlagConversationMutation,
  useRestoreThreadMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { cn } from "@/lib/utils";

interface ThreadOptionsMenuProps {
  contactId?: string;
  conversationId?: string;
  flagged?: boolean;
  archived?: boolean;
  triggerClassName?: string;
  onArchived?: () => void;
  onRestored?: () => void;
  onPurged?: () => void;
}

const ThreadOptionsMenu: React.FC<ThreadOptionsMenuProps> = ({
  contactId,
  conversationId,
  flagged = false,
  archived = false,
  triggerClassName,
  onArchived,
  onRestored,
  onPurged,
}) => {
  const [archiveThread, { isLoading: isArchiving }] = useArchiveThreadMutation();
  const [restoreThread] = useRestoreThreadMutation();
  const [flagConversation] = useFlagConversationMutation();
  const [deleteConversationPermanently, { isLoading: isPurging }] = useDeleteConversationPermanentlyMutation();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const stop = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleArchive = async () => {
    if (!contactId) {
      return;
    }
    try {
      await archiveThread(contactId).unwrap();
      toast.success("Conversation archived");
      setConfirmArchive(false);
      onArchived?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to archive conversation"));
    }
  };

  const handleRestore = async () => {
    if (!contactId) {
      return;
    }
    try {
      await restoreThread(contactId).unwrap();
      toast.success("Conversation restored");
      onRestored?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore conversation"));
    }
  };

  const handleToggleFlag = async () => {
    if (!conversationId) {
      return;
    }
    try {
      await flagConversation({ id: conversationId, flagged: !flagged }).unwrap();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update the flag"));
    }
  };

  const handlePurge = async () => {
    if (!conversationId) {
      return;
    }
    try {
      await deleteConversationPermanently(conversationId).unwrap();
      toast.success("Texts deleted permanently");
      setConfirmPurge(false);
      onPurged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete conversation"));
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-7 w-7 shrink-0", triggerClassName)}
            onClick={stop}
            title="Conversation options"
            aria-label="Conversation options"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={stop} onCloseAutoFocus={(event) => event.preventDefault()}>
          {archived ? (
            <>
              {contactId && (
                <DropdownMenuItem onClick={() => void handleRestore()}>
                  <ArchiveRestore className="mr-2 h-4 w-4" />
                  Restore
                </DropdownMenuItem>
              )}
              {conversationId && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirmPurge(true)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete texts permanently
                  </DropdownMenuItem>
                </>
              )}
            </>
          ) : (
            <>
              {conversationId && (
                <DropdownMenuItem onClick={() => void handleToggleFlag()}>
                  <Flag className="mr-2 h-4 w-4" fill={flagged ? "currentColor" : "none"} />
                  {flagged ? "Remove flag" : "Flag to come back to"}
                </DropdownMenuItem>
              )}
              {contactId && (
                <DropdownMenuItem onClick={() => setConfirmArchive(true)}>
                  <Archive className="mr-2 h-4 w-4" />
                  Archive
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent onClick={stop}>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive conversation</AlertDialogTitle>
            <AlertDialogDescription>
              The texts and calls with this contact leave your inbox and their thread. Nothing is deleted, and everything comes back on its own the next time they text or call.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleArchive()} disabled={isArchiving}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent onClick={stop}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete texts permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Every text and attachment in this conversation is deleted for good. Calls stay in the call log, and consent and do-not-call records for the number stay. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handlePurge()}
              disabled={isPurging}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ThreadOptionsMenu;
